import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import SessionRestoreDegraded from './SessionRestoreDegraded';

function ProtectedRoute({ children, requiredRole = null, allowedRoles = null }) {
  const { isAuthenticated, isLoading, user, authStatus } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // #3406: a stored session that could not be verified is neither "signed
  // in" nor "signed out". Redirecting to /login here used to read as a
  // sign-out; show the degraded panel (retry / sign in again) instead.
  if (authStatus === 'degraded') { // AUTH_STATUS.DEGRADED (literal: test mocks of AuthContext export useAuth only)
    return <SessionRestoreDegraded />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // SECURITY (Issue #282): Guest users can only access homepage and /chat routes
  // Redirect guests away from dashboard and other protected routes
  if (user && user.is_guest === true) {
    const currentPath = location.pathname;
    // Guests can access: homepage (/), /chat routes, /terms, /privacy
    const guestAllowedPaths = ['/', '/chat', '/terms', '/privacy', '/scenarios'];
    const isPathAllowedForGuest = guestAllowedPaths.some(
      (allowedPath) => currentPath === allowedPath || currentPath.startsWith(allowedPath + '/')
    );
    // Allow exact match on / or /chat/* paths
    const isExactHomeOrChat = currentPath === '/' || currentPath.startsWith('/chat');
    if (!isPathAllowedForGuest && !isExactHomeOrChat) {
      return <Navigate to="/" replace />;
    }
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole) {
    return (
      <div className="access-denied" data-testid="access-denied">
        <div className="access-denied-container">
          <div className="access-denied-icon">🚫</div>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>Required role: <strong>{requiredRole}</strong></p>
          <p>Your role: <strong>{user.role}</strong></p>
          {/* #1359: window.history.back() is correct here. This is an access-denied
              wall, not a detail-view with a knowable parent. The user arrived here
              after being denied — sending them back to wherever they came from is the
              right behavior. useSmartBack requires a stable default parent path,
              which doesn't exist for a generic role-guard component. */}
          <button onClick={() => window.history.back()} className="btn-back">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check if user role is in allowed roles list
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="access-denied" data-testid="access-denied">
        <div className="access-denied-container">
          <div className="access-denied-icon">🚫</div>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>Allowed roles: <strong>{allowedRoles.join(', ')}</strong></p>
          <p>Your role: <strong>{user.role}</strong></p>
          {/* #1359: same rationale as requiredRole block above — plain back is correct
              for a generic role-guard; useSmartBack is not applicable. */}
          <button onClick={() => window.history.back()} className="btn-back">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User has access, render the protected content
  return children;
}

// Convenience components for common role checks
// Issue #325: org_admin sits between facilitator and platform admin.
// AdminOnlyRoute remains platform-admin only (used for platform-level settings).
export function AdminOnlyRoute({ children }) {
  return (
    <ProtectedRoute requiredRole="admin">
      {children}
    </ProtectedRoute>
  );
}

export function FacilitatorRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
      {children}
    </ProtectedRoute>
  );
}

export function LearnerRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['learner', 'facilitator', 'org_admin', 'admin']}>
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;